const locales = require("./config/i18n");

// Decode HTML entities to their actual symbols in meta tags
const decodeMetaTags = () => {
  const htmlEntities = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
    "&#x27;": "'",
    "&apos;": "'",
    "&nbsp;": " ",
    "&iexcl;": "¡",
    "&iquest;": "¿",
    "&cent;": "¢",
    "&pound;": "£",
    "&yen;": "¥",
    "&euro;": "€",
    "&copy;": "©",
    "&reg;": "®",
    "&trade;": "™",
  };

  const decodeNumericEntities = (str) => {
    return str
      .replace(/&#(\d+);/g, (match, dec) => String.fromCodePoint(parseInt(dec, 10)))
      .replace(/&#x([a-f0-9]+);/gi, (match, hex) => String.fromCodePoint(parseInt(hex, 16)));
  };

  const decodeFull = (text) => {
    let decoded = text;
    let previous = "";
    let iterations = 0;
    const maxIterations = 10;

    while (previous !== decoded && iterations < maxIterations) {
      previous = decoded;
      Object.entries(htmlEntities).forEach(([entity, char]) => {
        decoded = decoded.replace(new RegExp(entity, "g"), char);
      });
      decoded = decodeNumericEntities(decoded);
      iterations++;
    }
    return decoded;
  };

  // Get all meta tags and decode their content
  const metaTags = document.querySelectorAll('meta[content]');
  metaTags.forEach((tag) => {
    const content = tag.getAttribute('content');
    if (content) {
      tag.setAttribute('content', decodeFull(content));
    }
  });

  // Also decode title tag
  if (document.title) {
    document.title = decodeFull(document.title);
  }
};

/**
 * INP (Interaction to Next Paint) monitoring
 * Tracks the worst interaction duration on the current page.
 * Logs automatically on every Gatsby route change (page navigation)
 * AND when the tab is closed/hidden.
 */
let maxINP = 0;
let currentPath = "";

function reportINP() {
  if (maxINP === 0) return;
  const rating =
    maxINP <= 200 ? "good" : maxINP <= 500 ? "needs-improvement" : "poor";
  const value = Math.round(maxINP);

  // console.log is stripped automatically in production builds by gatsby-plugin-remove-console,
  // so this only appears in your local dev browser — never publicly visible.
  console.log(`[INP] ${value}ms — ${rating} (${currentPath})`);

  maxINP = 0; // reset for the next page
}

exports.onClientEntry = () => {
  // Decode HTML entities in meta tags on initial page load
  decodeMetaTags();

  if (typeof PerformanceObserver === "undefined") return;
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > maxINP) {
          maxINP = entry.duration;
        }
      }
    });
    observer.observe({ type: "event", buffered: true, durationThreshold: 40 });

    // Catch the case where the user closes the tab without navigating away
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") reportINP();
    });
  } catch (e) {
    // Browser doesn't support event-timing; ignore
  }
};

exports.onRouteUpdate = ({ location }) => {
  // Log INP for the page we just left, then update the tracked path
  reportINP();
  currentPath = location.pathname;

  // Decode HTML entities in meta tags to show actual symbols
  decodeMetaTags();
  // Extract locale paths from imported locales
  const localePaths = Object.values(locales).map((locale) => locale.path);

  // Extract locale from pathname
  const locale = localePaths.find((loc) =>
    location.pathname.startsWith(`/${loc}/`),
  );

  // Handle redirects with or without locale
  const basePath = locale
    ? location.pathname.replace(`/${locale}`, "")
    : location.pathname;
  const queryString = location.search || "";

  // Handle /media-center/press-releases/* and /media-center/press_releases/*
  if (basePath.match(/^\/media-center\/press[-_]?releases\//)) {
    const newBasePath = basePath.replace(
      /^\/media-center\/press[-_]?releases\//,
      "/about/news-and-updates/press-releases/",
    );
    const newPath = locale ? `/${locale}${newBasePath}` : newBasePath;
    window.location.replace(`${newPath}${queryString}`);
    return;
  }

  if (basePath.match(/^\/about\/news-and-updates\/press_releases\//)) {
    const newBasePath = basePath.replace(
      /^\/about\/news-and-updates\/press_releases\//,
      "/about/news-and-updates/press-releases/",
    );
    const newPath = locale ? `/${locale}${newBasePath}` : newBasePath;
    window.location.replace(`${newPath}${queryString}`);
    return;
  }

  // Handle /media-center/insights/*
  if (basePath.startsWith("/media-center/insights/")) {
    const newBasePath = basePath.replace(
      /^\/media-center\/insights\//,
      "/insights/",
    );
    const newPath = locale ? `/${locale}${newBasePath}` : newBasePath;
    window.location.replace(`${newPath}${queryString}`);
    return;
  }

  // Static redirects
  const redirects = {
    "/media-center/events/": "/insights/",
    "/media-center/in_the_news/": "/insights/",
    "/about/triumphs/": "/about/awards/",
    "/media-center/press-releases/": "/about/news-and-updates/press-releases/",
    "/media-center/press_releases/": "/about/news-and-updates/press-releases/",
  };

  if (redirects[basePath]) {
    const newPath = locale
      ? `/${locale}${redirects[basePath]}`
      : redirects[basePath];

    window.location.replace(`${newPath}${queryString}`);
  }
};
